import { create } from '@bufbuild/protobuf';
import { createClient } from '@connectrpc/connect';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateInvitationRequestSchema,
  DeleteUserRequestSchema,
  GetCurrentUserRequestSchema,
  GetUserRequestSchema,
  ListAccessRulesRequestSchema,
  ListUsersRequestSchema,
  PreviewAccessRuleMatchRequestSchema,
  ResendInvitationRequestSchema,
  RevokeInvitationRequestSchema,
  UpdateUserAccessRequestSchema,
  UpdateUserStatusRequestSchema,
  UserService,
  type UserStatus,
} from '../gen/tenants/v1/tenants_pb';
import { createApiTransport, createAuthApiTransport } from '../transport';

function transport(token?: string | null) {
  return token ? createAuthApiTransport(token) : createApiTransport();
}

export interface ListUsersParams {
  search?: string;
  page?: number;
  pageSize?: number;
  roleIdFilter?: string;
  statusFilter?: UserStatus;
  platformAdminsOnly?: boolean;
  sortBy?: 'name' | 'last_active' | 'created_at';
  sortDir?: 'asc' | 'desc';
}

/**
 * Hook to list users within a specific tenant with optional server-side search, filter, and pagination.
 * Pass platformAdminsOnly (with no tenantId) to list the platform's tenant-less Admins instead.
 *
 * @param tenantId - The tenant ID to list users for. Ignored when params.platformAdminsOnly is set.
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @param params - Optional search, filter, and pagination params
 * @returns Query result with list of users and totalCount
 */
export function useListUsers(tenantId: string, token?: string | null, params?: ListUsersParams) {
  const platformAdminsOnly = params?.platformAdminsOnly ?? false;
  return useQuery({
    queryKey: [
      'users',
      'list',
      platformAdminsOnly ? 'platform_admins' : tenantId,
      !!token,
      params?.search ?? '',
      params?.page ?? 1,
      params?.pageSize ?? 20,
      params?.roleIdFilter ?? '',
      params?.statusFilter ?? 0,
      params?.sortBy ?? 'name',
      params?.sortDir ?? 'asc',
    ],
    queryFn: async () => {
      const client = createClient(UserService, transport(token));
      const request = create(ListUsersRequestSchema, {
        tenantId: platformAdminsOnly ? '' : tenantId,
        search: params?.search ?? '',
        page: params?.page ?? 1,
        pageSize: params?.pageSize ?? 20,
        roleIdFilter: params?.roleIdFilter ?? '',
        statusFilter: params?.statusFilter,
        platformAdminsOnly,
        sortBy: params?.sortBy ?? 'name',
        sortDir: params?.sortDir ?? 'asc',
      });
      return await client.listUsers(request);
    },
    enabled: platformAdminsOnly ? !!token : !!tenantId && !!token,
  });
}

/**
 * Hook to retrieve a single user by ID.
 *
 * @param id - The user ID to retrieve
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Query result with user data
 */
export function useGetUser(id: string, token?: string | null) {
  return useQuery({
    queryKey: ['users', id, !!token],
    queryFn: async () => {
      const client = createClient(UserService, transport(token));
      const request = create(GetUserRequestSchema, { id });
      return await client.getUser(request);
    },
    enabled: !!id && !!token,
  });
}

/**
 * Hook to retrieve the DB-authoritative record for the calling user
 * (resolved server-side by authenticated email, not a request parameter).
 * The source of truth for role-based UI gating — never derive authorization
 * from Auth0 JWT claims directly.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Query result with the current user's data; errors (e.g. NotFound)
 *   mean the identity has no provisioned account.
 */
export function useGetCurrentUser(token?: string | null) {
  return useQuery({
    queryKey: ['users', 'me', !!token],
    queryFn: async () => {
      const client = createClient(UserService, transport(token));
      const request = create(GetCurrentUserRequestSchema, {});
      return await client.getCurrentUser(request);
    },
    enabled: !!token,
    retry: false,
  });
}

export interface AccessRuleInputData {
  effect?: 'allow' | 'deny';
  restrictionKind: 'id_based' | 'rule_based';
  resourceIds?: string[];
  ruleConditionJson?: string;
  resourceKind: 'deployment' | 'workspace';
}

/**
 * Mutation hook to invite a new user: a tenant member (role_id + tenant_id)
 * or a new Operator Admin (role_id only, no tenant_id).
 * Invalidates the relevant users list cache regardless of outcome — even
 * when the invite email fails to send, the user row is committed
 * server-side and should show up in the list right away (see
 * ErrInvitationEmailFailed handling in InviteMemberPage).
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Mutation result for creating an invitation
 */
export function useCreateInvitation(token?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      email: string;
      name?: string;
      roleId: string;
      tenantId?: string;
      accessRules?: AccessRuleInputData[];
    }) => {
      const client = createClient(UserService, transport(token));
      const request = create(CreateInvitationRequestSchema, {
        email: input.email,
        name: input.name ?? '',
        roleId: input.roleId,
        tenantId: input.tenantId ?? '',
        accessRules: (input.accessRules ?? []).map((r) => ({ ...r, effect: r.effect ?? 'allow' })),
      });
      return await client.createInvitation(request);
    },
    onSettled: (_data, _error, variables) => {
      const listKey = variables.tenantId ? variables.tenantId : 'platform_admins';
      queryClient.invalidateQueries({ queryKey: ['users', 'list', listKey] });
    },
  });
}

/**
 * Mutation hook to re-issue a still-pending invitation's email.
 * On success, invalidates the users list cache.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Mutation result for resending an invitation
 */
export function useResendInvitation(token?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string }) => {
      const client = createClient(UserService, transport(token));
      const request = create(ResendInvitationRequestSchema, { userId: input.userId });
      return await client.resendInvitation(request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', 'list'] });
    },
  });
}

/**
 * Mutation hook to cancel a still-pending invitation.
 * On success, invalidates the users list cache.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Mutation result for revoking an invitation
 */
export function useRevokeInvitation(token?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string }) => {
      const client = createClient(UserService, transport(token));
      const request = create(RevokeInvitationRequestSchema, { userId: input.userId });
      return await client.revokeInvitation(request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', 'list'] });
    },
  });
}

/**
 * Hook to list every access_rules row tied to one user, for the
 * member-detail view.
 *
 * @param userId - The user ID to list access rules for
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Query result with the list of access rules
 */
export function useListAccessRules(userId: string, token?: string | null) {
  return useQuery({
    queryKey: ['users', userId, 'access-rules', !!token],
    queryFn: async () => {
      const client = createClient(UserService, transport(token));
      const request = create(ListAccessRulesRequestSchema, { userId });
      return await client.listAccessRules(request);
    },
    enabled: !!userId && !!token,
  });
}

/**
 * Mutation hook to evaluate a candidate (not-yet-saved) restriction against
 * a tenant's current workspaces/deployments — the restriction builder's
 * live-preview panel. A mutation (not a query) since it's triggered
 * on-demand as the builder's fields change, typically behind a debounce,
 * rather than cached against a stable key.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Mutation result with the list of currently-matching resources
 */
export function usePreviewAccessRuleMatch(token?: string | null) {
  return useMutation({
    mutationFn: async (input: {
      tenantId: string;
      restrictionKind: 'id_based' | 'rule_based';
      resourceIds?: string[];
      ruleConditionJson?: string;
    }) => {
      const client = createClient(UserService, transport(token));
      const request = create(PreviewAccessRuleMatchRequestSchema, {
        tenantId: input.tenantId,
        restrictionKind: input.restrictionKind,
        resourceIds: input.resourceIds ?? [],
        ruleConditionJson: input.ruleConditionJson ?? '',
      });
      return await client.previewAccessRuleMatch(request);
    },
  });
}

/**
 * Mutation hook to update an existing member's access in a single request:
 * any combination of display name, primary role, new access_rules to
 * create, and existing access_rules to revoke — all applied server-side in
 * one transaction and recorded as one audit event, rather than one RPC (and
 * one audit event) per sub-change. Pass only the fields that changed; an
 * empty roleId clears the primary role back to restricted-only (rejected
 * server-side if the member would then have no access_rules to fall back
 * on). On success, invalidates that user's cache entries.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Mutation result with the updated user
 */
export function useUpdateUserAccess(token?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      userId: string;
      name?: string;
      roleId?: string;
      rulesToCreate?: AccessRuleInputData[];
      ruleIdsToRevoke?: string[];
    }) => {
      const client = createClient(UserService, transport(token));
      const request = create(UpdateUserAccessRequestSchema, {
        userId: input.userId,
        name: input.name,
        roleId: input.roleId,
        rulesToCreate: (input.rulesToCreate ?? []).map((r) => ({
          ...r,
          effect: r.effect ?? 'allow',
        })),
        ruleIdsToRevoke: input.ruleIdsToRevoke ?? [],
      });
      return await client.updateUserAccess(request);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users', variables.userId, 'access-rules'] });
      queryClient.invalidateQueries({ queryKey: ['users', variables.userId] });
      queryClient.invalidateQueries({ queryKey: ['users', 'list'] });
    },
  });
}

/**
 * Mutation hook to enable or disable a user account. Disabling blocks the
 * account from authenticating starting with its next request; it never
 * touches Auth0. On success, invalidates that user's cache entries.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Mutation result with the updated user
 */
export function useUpdateUserStatus(token?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; status: UserStatus }) => {
      const client = createClient(UserService, transport(token));
      const request = create(UpdateUserStatusRequestSchema, {
        id: input.id,
        status: input.status,
      });
      return await client.updateUserStatus(request);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['users', 'list'] });
    },
  });
}

/**
 * Mutation hook to permanently delete an already-disabled user account: a
 * soft-delete in Sunstone paired with a hard-delete of their Auth0
 * identity. The account must already be disabled (see useUpdateUserStatus)
 * — terminal, no undelete. On success, invalidates that user's cache
 * entries.
 *
 * @param token - Auth0 access token (required for authenticated endpoints)
 * @returns Mutation result for deleting a user
 */
export function useDeleteUser(token?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) => {
      const client = createClient(UserService, transport(token));
      const request = create(DeleteUserRequestSchema, { id: input.id });
      return await client.deleteUser(request);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['users', 'list'] });
    },
  });
}
